import React from "react";
import { Box, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import type { ClinicalPhase } from "./types";

export interface ClinicalPhasePillProps {
  /** The clinical phase to display */
  phase: ClinicalPhase;
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

const PHASE_LABELS: Record<ClinicalPhase, string> = {
  maintenance: "Maintenance",
  intervention: "Intervention",
  baseline: "Baseline",
  generalization: "Generalization",
};

const getPhaseColor = (theme: Theme): Record<ClinicalPhase, string> => {
  const p = theme.palette as any;
  return {
    maintenance: p.green?.[500] ?? theme.palette.success.light,
    intervention: p.blue?.[500] ?? "#2196f3",
    baseline: p.grayscale?.[500] ?? theme.palette.text.secondary,
    generalization: p.purple?.[500] ?? "#8E42CA",
  };
};

/**
 * Solid-colored pill badge representing the clinical phase of a target.
 * Maintenance is green, Intervention is blue, Generalization is purple,
 * and Baseline is grey.
 *
 * @param props
 * @param props.phase - The clinical phase id
 * @param [props.sx]  - Additional MUI sx overrides
 */
const ClinicalPhasePill: React.FC<ClinicalPhasePillProps> = ({ phase, sx }) => {
  return (
    <Box
      component="span"
      aria-label={`Clinical phase: ${PHASE_LABELS[phase]}`}
      sx={[
        (theme) => ({
          display: "inline-flex",
          alignItems: "center",
          px: "3px",
          py: "2px",
          borderRadius: "6px",
          bgcolor: getPhaseColor(theme)[phase],
          overflow: "hidden",
          flexShrink: 0,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        component="span"
        sx={{
          px: "6px",
          fontSize: "13px",
          lineHeight: "18px",
          letterSpacing: "0.16px",
          fontWeight: 400,
          color: "primary.contrastText",
          whiteSpace: "nowrap",
        }}
      >
        {PHASE_LABELS[phase]}
      </Typography>
    </Box>
  );
};

export default ClinicalPhasePill;
